import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';

// material-ui
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// project import
import useConfig from 'hooks/useConfig';
import useSettings from 'hooks/useSettings';
import useTelegramTheme from 'hooks/useTelegramTheme';
import { ThemeMode } from 'config';
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';

// ==============================|| DEFAULT THEME - MAIN  ||============================== //

export default function ThemeCustomization({ children }) {
  const { themeDirection, mode, presetColor, fontFamily, onChangeMode } = useConfig();
  const { settings, loading } = useSettings();
  const tgTheme = useTelegramTheme();
  const themeAppliedRef = useRef(false);

  // Telegram colorScheme drives the mode whenever the app runs inside TG —
  // the user's TG light/dark choice always wins over the saved app setting.
  useEffect(() => {
    if (!tgTheme.available || !tgTheme.colorScheme) return;
    const desired = tgTheme.colorScheme === 'dark' ? ThemeMode.DARK : ThemeMode.LIGHT;
    if (mode !== desired) onChangeMode(desired);
    themeAppliedRef.current = true;
  }, [tgTheme.available, tgTheme.colorScheme, mode, onChangeMode]);

  // Fallback to saved DB setting only when not running inside Telegram.
  useEffect(() => {
    if (loading || themeAppliedRef.current || tgTheme.available) return;

    if (settings && typeof settings.theme_dark === 'boolean') {
      onChangeMode(settings.theme_dark ? ThemeMode.DARK : ThemeMode.LIGHT);
    }

    themeAppliedRef.current = true;
  }, [loading, settings, onChangeMode, tgTheme.available]);

  const theme = useMemo(() => Palette(mode, presetColor), [mode, presetColor]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const themeTypography = useMemo(() => Typography(fontFamily), [fontFamily]);
  const themeCustomShadows = useMemo(() => CustomShadows(theme), [theme]);

  const palette = useMemo(() => {
    const p = { ...theme.palette };
    const tg = tgTheme.params;
    if (tg.bg_color || tg.secondary_bg_color) {
      p.background = {
        ...p.background,
        ...(tg.bg_color ? { default: tg.bg_color } : {}),
        ...(tg.secondary_bg_color ? { paper: tg.secondary_bg_color } : {})
      };
    }
    if (tg.text_color || tg.hint_color) {
      p.text = {
        ...p.text,
        ...(tg.text_color ? { primary: tg.text_color } : {}),
        ...(tg.hint_color ? { secondary: tg.hint_color } : {})
      };
    }
    if (tg.link_color) {
      p.divider = tg.hint_color ?? p.divider;
    }
    return p;
  }, [theme.palette, tgTheme.params]);

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: 1,
          paddingTop: 1,
          paddingBottom: 8
        }
      },
      palette,
      customShadows: themeCustomShadows,
      typography: themeTypography
    }),
    [themeDirection, palette, themeTypography, themeCustomShadows]
  );

  const themes = createTheme(themeOptions);
  themes.components = componentsOverride(themes);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = {
  children: PropTypes.node
};
